import React from 'react';
import '../../styles/Loading.css';

/**
 * CSS-based spinner component - consistent double-line spinner
 * Sizes: 'sm' (14px), 'md' (18px - default), 'lg' (24px), 'xl' (32px)
 */
export function Spinner({ size = 'md', className = '' }) {
  const sizeClass = size === 'md' ? '' : `spinner-${size}`;
  return <span className={`css-spinner ${sizeClass} ${className}`} />;
}

/**
 * Skeleton loader for text content
 */
export function SkeletonText({ width = '100%', height = '1rem', className = '' }) {
  return (
    <div 
      className={`skeleton skeleton-text ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * Skeleton loader for circular elements (avatars, icons)
 */
export function SkeletonCircle({ size = 40, className = '' }) {
  return (
    <div 
      className={`skeleton skeleton-circle ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/**
 * Skeleton loader for rectangular blocks
 */
export function SkeletonBlock({ width = '100%', height = '100px', className = '' }) {
  return (
    <div 
      className={`skeleton skeleton-block ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * Skeleton loader for form fields
 */
export function SkeletonFormField({ labelWidth = '30%', className = '' }) {
  return (
    <div className={`skeleton-form-field ${className}`}>
      <SkeletonText width={labelWidth} height="0.875rem" />
      <SkeletonBlock height="40px" />
    </div>
  );
}

/**
 * Skeleton loader for table rows
 */
export function SkeletonTableRow({ columns = 4, className = '' }) {
  return (
    <tr className={`skeleton-table-row ${className}`}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i}>
          <SkeletonText width={i === 0 ? '80%' : '60%'} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Skeleton loader for cards
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={`skeleton-card ${className}`}>
      <div className="skeleton-card-header">
        <SkeletonCircle size={40} />
        <div className="skeleton-card-title">
          <SkeletonText width="70%" height="1rem" />
          <SkeletonText width="50%" height="0.75rem" />
        </div>
      </div>
      <div className="skeleton-card-body">
        <SkeletonText width="100%" />
        <SkeletonText width="90%" />
        <SkeletonText width="75%" />
      </div>
    </div>
  );
}

/**
 * Inline loading spinner
 */
export function LoadingSpinner({ size = 'md', className = '' }) {
  return <Spinner size={size} className={className} />;
}

/**
 * Full-page loading overlay
 */
export function LoadingOverlay({ message = 'Loading...' }) {
  return (
    <div className="loading-overlay">
      <div className="loading-overlay-content">
        <Spinner size="xl" />
        <span>{message}</span>
      </div>
    </div>
  );
}

/**
 * Section loading state
 */
export function LoadingSection({ message = 'Loading...', height = '200px' }) {
  return (
    <div className="loading-section" style={{ minHeight: height }}>
      <Spinner size="lg" />
      <span>{message}</span>
    </div>
  );
}

/**
 * Skeleton for settings/form pages
 */
export function SkeletonSettings() {
  return (
    <div className="skeleton-settings">
      <div className="skeleton-settings-header">
        <SkeletonText width="200px" height="1.5rem" />
        <SkeletonText width="300px" height="0.875rem" />
      </div>
      <div className="skeleton-settings-group">
        <SkeletonFormField labelWidth="100px" />
        <SkeletonFormField labelWidth="120px" />
        <SkeletonFormField labelWidth="80px" />
      </div>
      <div className="skeleton-settings-group">
        <SkeletonFormField labelWidth="140px" />
        <SkeletonFormField labelWidth="100px" />
      </div>
    </div>
  );
}

/**
 * Skeleton for list items
 */
export function SkeletonListItem({ hasAvatar = true, className = '' }) {
  return (
    <div className={`skeleton-list-item ${className}`}>
      {hasAvatar && <SkeletonCircle size={36} />}
      <div className="skeleton-list-content">
        <SkeletonText width="60%" height="1rem" />
        <SkeletonText width="40%" height="0.75rem" />
      </div>
      <SkeletonText width="60px" height="1rem" />
    </div>
  );
}

// Export all components as named object
export const LoadingComponents = {
  Spinner,
  SkeletonText,
  SkeletonCircle,
  SkeletonBlock,
  SkeletonFormField,
  SkeletonTableRow,
  SkeletonCard,
  LoadingSpinner,
  LoadingOverlay,
  LoadingSection,
  SkeletonSettings,
  SkeletonListItem
};

export default LoadingComponents;
